import { useQuery } from '@tanstack/react-query';
import {
  fetchActiveWorktreesByAgentConfig,
  fetchProjectRepositories,
  fetchProjectRepositoryBranches,
} from '../lib/api';
import type { RunnerRequestDataAdapter } from '../lib/runnerRequest';
import { webRunnerRequestSelection as selection } from '../lib/webRunnerRequestSelection';
import { queryKeys } from './queries/queryKeys';
import { useDaemonTriggerResolvedSettings } from './queries/useDaemonTriggers';
import { useDiscoveredWorktrees } from './useWorktreeList';

type WebRunnerRequestDataAdapter = RunnerRequestDataAdapter<
  Awaited<ReturnType<typeof fetchProjectRepositories>>[number],
  Awaited<ReturnType<typeof fetchProjectRepositoryBranches>>[number],
  Awaited<ReturnType<typeof fetchActiveWorktreesByAgentConfig>>[number],
  NonNullable<ReturnType<typeof useDiscoveredWorktrees>['data']>[number],
  NonNullable<Parameters<typeof useDaemonTriggerResolvedSettings>[1]>,
  NonNullable<ReturnType<typeof useDaemonTriggerResolvedSettings>['data']>
>;

export const webRunnerRequestDataAdapter: WebRunnerRequestDataAdapter = {
  useRepositories: (token, projectId, enabled) =>
    useQuery({
      queryKey: queryKeys.projects.repositories(projectId),
      queryFn: () => fetchProjectRepositories(token ?? '', projectId),
      enabled: Boolean(token) && projectId.length > 0 && enabled,
    }),
  useBranches: (token, projectId, repositoryId, enabled) =>
    useQuery({
      queryKey: queryKeys.projects.repositoryBranches(projectId, repositoryId),
      queryFn: () => fetchProjectRepositoryBranches(token ?? '', projectId, repositoryId),
      enabled: Boolean(token) && projectId.length > 0 && repositoryId.length > 0 && enabled,
    }),
  useActiveWorktrees: (token, agentConfigId, enabled) =>
    useQuery({
      queryKey: queryKeys.daemonTriggers.activeWorktrees(agentConfigId),
      queryFn: () => fetchActiveWorktreesByAgentConfig(token ?? '', agentConfigId),
      enabled: Boolean(token) && agentConfigId.length > 0 && enabled,
      staleTime: 30_000,
    }),
  useDiscoveredWorktrees: (token, projectId) => useDiscoveredWorktrees(token, projectId),
  useResolvedSettings: (token, input, options) => useDaemonTriggerResolvedSettings(token, input, options),
  selection,
};
